import type { QueryCacheUserChangeEvent, QueryCacheUserServiceDataSource } from './index';
import { effect } from '@preact/signals-react';
import { getCurrentIsSuperuserEnabledFlag } from 'src/store/selectors';
import type { IsomorphicBindings } from 'src/types/bindings';

export function createStoreCurrentUserQueryCacheUserServiceDataSource(
    store: IsomorphicBindings['Store'],
    currentUserModel: IsomorphicBindings['CurrentUserModel'],
): QueryCacheUserServiceDataSource {
    return {
        subscribe(callback) {
            const disposableStack = new DisposableStack();
            let isSuperuserEnabled = false;
            let previousIdentityFingerprint: string | undefined;
            const emitIdentity = () => {
                const effectiveUserId = currentUserModel.userId.peek();
                const fingerprint = JSON.stringify({ userId: effectiveUserId, isSuperuserEnabled });
                if (fingerprint === previousIdentityFingerprint) return;

                previousIdentityFingerprint = fingerprint;
                callback({
                    kind: 'identity',
                    effectiveUserId,
                    fingerprint,
                } satisfies QueryCacheUserChangeEvent);
            };

            disposableStack.defer(
                store
                    .subscribeSelector(getCurrentIsSuperuserEnabledFlag, { leading: true })
                    .handle((nextIsSuperuserEnabled) => {
                        isSuperuserEnabled = nextIsSuperuserEnabled;
                        emitIdentity();
                    }),
            );
            disposableStack.defer(
                effect(() => {
                    void currentUserModel.userId.value;
                    emitIdentity();
                }),
            );

            let previousEntitlementsRevision = currentUserModel.entitlementsRevision.peek();
            disposableStack.defer(
                effect(() => {
                    const nextEntitlementsRevision = currentUserModel.entitlementsRevision.value;
                    if (nextEntitlementsRevision === previousEntitlementsRevision) return;

                    previousEntitlementsRevision = nextEntitlementsRevision;
                    callback({
                        kind: 'entitlements',
                        effectiveUserId: currentUserModel.userId.peek(),
                        fingerprint: String(nextEntitlementsRevision),
                    } satisfies QueryCacheUserChangeEvent);
                }),
            );

            return () => disposableStack.dispose();
        },
    };
}
